import './MobileDrawer.scss';

import { Drawer } from 'antd';
import { ReactNode, UIEventHandler, useEffect, useRef } from 'react';

import FullscreenDrawer from './FullscreenDrawer';
import { useResizableDrawer } from './useResizableDrawer';

interface MobileDrawerProps {
  title?: ReactNode;
  children: ReactNode;
  open: boolean;
  setOpen: Function;
  maskClosable?: boolean;
  footer?: ReactNode;
  destroyOnHidden?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  height?: 'large' | 'middle' | number | string;
  fullscreen?: boolean;
  onBodyScroll?: UIEventHandler<HTMLElement>;
}

const MobileDrawer = (props: MobileDrawerProps) => {
  const {
    title,
    children,
    open,
    setOpen,
    maskClosable,
    footer,
    destroyOnHidden,
    fullscreen,
    onBodyScroll,
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const { handleMousedown, height } = useResizableDrawer(props, ref);

  useEffect(() => {
    if (!open || !onBodyScroll) return;
    if (!ref.current) return;
    const drawerBody = ref.current
      .closest('.ant-drawer-content-wrapper')
      ?.querySelector('.ant-drawer-body');

    if (drawerBody) {
      const scrollHandler = (event: Event) => {
        onBodyScroll(event as unknown as React.UIEvent<HTMLElement>);
      };

      drawerBody.addEventListener('scroll', scrollHandler);

      return () => drawerBody.removeEventListener('scroll', scrollHandler);
    }
  }, [open, onBodyScroll]);

  return fullscreen ? (
    <FullscreenDrawer
      title={title}
      open={open}
      setOpen={setOpen}
      footer={footer}
      destroyOnHidden={destroyOnHidden}>
      {children}
    </FullscreenDrawer>
  ) : (
    <Drawer
      destroyOnHidden={destroyOnHidden}
      rootClassName={`MobileDrawer`}
      className={`MobileDrawerContent`}
      style={height ? { height: height } : {}}
      placement="bottom"
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      title={
        <div
          className="MobileDrawerHeader"
          onMouseDownCapture={handleMousedown}
          onTouchStartCapture={handleMousedown}>
          <div className="MobileDrawerTop">
            <div className="MobileDrawerResizeElement"></div>
          </div>
          <p className="MobileDrawerHeaderTitle">{title}</p>
        </div>
      }
      maskClosable={maskClosable || false}
      footer={footer}>
      {open && <div ref={ref}>{children}</div>}
    </Drawer>
  );
};

export default MobileDrawer;
